import type { z } from 'zod'
import { canonicalize } from '@gridfit/core/canonical'
import type { JsonValue } from '@gridfit/core/canonical'
import type { Capability, PrincipalKind } from '@gridfit/core/principals'
import type { CommandRequest, CommandResponse } from '@gridfit/contracts/wire'
import { markUpgradeRequired } from '../pwa/skew'

/**
 * THE one module in `packages/web/src` that calls `fetch` — web-pre-deploy
 * item 11, gated by `tools/structural/web-fetch-sites.test.ts`.
 *
 * The plan's File Structure table named one client module; what shipped was
 * ten `fetch` call sites across eight modules. Only this file knew what an
 * `ApiError` was or what to do with a 426, so every other site invented its
 * own error shape, and `/api/me` was requested TWICE at boot — on the boots
 * where `outbox/clearance.ts`'s detector actually asks, i.e. when the
 * install marker is MISSING; with a marker present it short-circuits to
 * `'intact'` and never fetches at all, so the duplicate was real but
 * narrower than "every boot". Every route the app speaks to now has exactly
 * one function here, and each caller keeps its own logic (the reporter's cap
 * and dedupe, the clearance detector's three-state reasoning, the login
 * form's handling) — only the transport moved.
 *
 * CYCLE, deliberate and safe: this file imports `markUpgradeRequired` from
 * `../pwa/skew` (the 426 half of the skew handshake) and `pwa/skew.ts` now
 * imports `fetchVersion` from here (the poll half). Neither module calls the
 * other at MODULE-EVALUATION time — both sides are function declarations,
 * hoisted and initialised before either module body runs — so whichever of
 * the two ESM reaches first, the partially-initialised namespace the other
 * sees is never read until a real request happens. Verified by the whole
 * `pnpm test:web` suite and by `pnpm web:build` (Rollup resolves it without
 * a warning).
 *
 * NOT unavoidable, and this file used to claim otherwise — review round 1's
 * Minor 3. All this file needs from `pwa/skew.ts` is `markUpgradeRequired`,
 * so lifting that module's STORE (`upgradeRequired`, `listeners`,
 * `setUpgradeRequired`, `markUpgradeRequired`) into a third module would
 * break the cycle with no `fetch` moving anywhere and the gate untouched.
 * Left as it is because the cycle is genuinely benign, not because there is
 * no way out — a future task that wants the store elsewhere for its own
 * reasons should know the option exists.
 */

/**
 * A request accepted and never answered would otherwise hang for the life of
 * the document — and since the cross-tab drain lock (`outbox/drain.ts`), a
 * hung `runCommand` holds that lock against every OTHER tab of the origin
 * too, not just this one. 30s matches `drain.ts`'s own `RETRY_BASE_MS`; an
 * aborted send leaves the row `queued` and the next trigger retries it with
 * the SAME stored idempotency key, so a server that did receive it dedupes
 * the replay. Hardcoded pending the config registry (ledger, "At 080").
 *
 * EXPORTED so the tests drive the REAL value rather than a copy that could
 * drift from it — the same reason `pwa/deviceMode.ts` exports its intervals.
 */
export const REQUEST_TIMEOUT_MS = 30_000

/**
 * Thrown for transport and server faults. A REFUSAL is not one of these —
 * see `commandHook`.
 *
 * `reason` — web-pre-deploy item 4's client half — is the machine-readable
 * companion to `message`, present only when the server's own error body
 * carried one. Today the only route that sends one is `POST
 * /api/cmd/:command`'s 401 (`reason: 'disabled'`, when the session resolved
 * but the principal row itself is `disabled`; absent for every other 401 —
 * `packages/api`'s own resolver, see `outbox/drain.ts`'s
 * `isRecoverableHttpFailure` for what a caller does with it). Optional and
 * generic rather than a literal union of one value: a caller that does not
 * know about `reason` sees exactly the `ApiError` it always has.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly reason?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Parses an error `Response` body into the human-readable `message` and,
 * when the server sent one, the machine-readable `reason` alongside it — see
 * `ApiError`'s own doc comment above for what that field carries and why.
 * Reads `reason` generically off any error body rather than special-casing
 * one route, so a future route that adds its own `reason` needs no change
 * here.
 */
async function readError(response: Response): Promise<{ message: string; reason?: string }> {
  try {
    const body = (await response.json()) as { error?: unknown; reason?: unknown }
    const message = typeof body.error === 'string' ? body.error : response.statusText
    return typeof body.reason === 'string' ? { message, reason: body.reason } : { message }
  } catch {
    return { message: response.statusText }
  }
}

/**
 * What a person sees when a 200 arrives whose BODY does not match the shape
 * the query declared — web-pre-deploy item 7.
 *
 * Deliberately names no query, no field and no zod code. CLAUDE.md forbids a
 * developer identifier on a user-facing screen, and this cycle already
 * reopened one finding for exactly that (the "no developer identifiers"
 * item, fixed in the entry label and missed in the synthesised refusal
 * message). The diagnostic that DOES name the query and the failing field
 * travels separately, on `ResultMismatchError`'s own fields, to
 * `reportClientError` — `api/hooks.ts`'s `reportResultMismatch` is the one
 * place that reads them.
 *
 * "the app needs updating" rather than "try again in a few minutes": a
 * response that fails its own declared schema is a DRIFT between what this
 * build expects and what the server now sends, and waiting does not fix a
 * drift. A reload picks up a newer client if one has shipped, which is the
 * one action a site manager can usefully take.
 */
const CANNOT_READ_RESPONSE =
  "This screen can't read what the server sent back. Try again, and if it keeps happening the app needs updating."

/**
 * A 200 whose body failed its query shape's `result` schema. NOT an
 * `ApiError`: that means the server faulted or refused at the transport
 * level and carries a `status`; this one means the request SUCCEEDED and the
 * answer was the wrong shape, which has no status to carry and needs a
 * different name so a caller can tell the two apart. (Nothing branches on
 * that distinction today beyond `api/hooks.ts`'s reporting — but note
 * `commandHook`'s offline branch already treats "not an `ApiError`" as
 * "the network dropped", so a contract fault masquerading as one would be a
 * genuine mis-classification if this ever moved onto the command path.)
 *
 * `message` is the human sentence above; `queryName` and `issue` are the
 * telemetry. Two fields rather than one baked-in string precisely so the
 * screen and the beacon cannot drift apart the way a single message would
 * force them to.
 */
export class ResultMismatchError extends Error {
  constructor(
    readonly queryName: string,
    readonly issue: string,
  ) {
    super(CANNOT_READ_RESPONSE)
    this.name = 'ResultMismatchError'
  }
}

/**
 * A compact, one-line rendering of a zod failure — `total: Invalid input:
 * expected number, received string`, not the multi-kilobyte JSON blob
 * `ZodError.message` is. Exported because the FRAGMENT path
 * (`api/hooks.ts`'s `commandHook` `onSuccess`) validates the identical
 * contract arriving over a different transport, and two hand-rolled
 * renderings of the same failure would make one report readable and the
 * other not.
 *
 * `(root)` for an empty path: a top-level failure (`expected object,
 * received null`) has no field name, and an empty string there reads as a
 * missing value rather than a whole-body mismatch.
 */
export function describeSchemaIssues(error: z.ZodError): string {
  return error.issues.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`).join('; ')
}

/**
 * `GET /api/q/:name`, validated against the shape's own `result` schema
 * before the value is handed back — web-pre-deploy item 7. Before this, the
 * declared `result` was a compile-time contract only (`queryHook`'s
 * `as R`), so a fold-shape drift reached the screen as `undefined` fields
 * with nothing anywhere to signal it.
 *
 * THE SCHEMA IS PASSED IN, not looked up here by name. Both were available —
 * `listShapes().queries` keyed by name would have worked (it is exactly what
 * the fragment path in `api/hooks.ts` has to do, having only a name to go
 * on) — and passing it in is the better seam for three reasons. It is the
 * only one that cannot MISS: `queryHook` already holds the shape, so the
 * name and the schema come from the SAME object and cannot disagree, where a
 * lookup introduces a "no shape registered under that name" branch that has
 * to decide, at runtime, between refusing a legitimate response and letting
 * an unvalidated one through. It keeps this module free of a value import
 * from `@gridfit/contracts/shapes` — this file is transport, and the
 * registry is not something a transport needs to consult. And it makes the
 * return type honest: `Promise<R>` rather than `Promise<unknown>` plus a
 * cast at the call site, which deletes the very cast this item exists to
 * stop trusting.
 *
 * Returns `parsed.data`, not the raw body: zod STRIPS unknown keys by
 * default, so a server that ADDS a field still parses clean and the client
 * simply ignores the addition. Only a missing or wrong-typed field trips
 * this — ordinary additive server change stays safe, which is the property
 * `test/result-schema.test.tsx`'s second case pins.
 *
 * Does NOT report. The beacon call lives in `api/hooks.ts`'s `queryHook`,
 * with the fragment path's identical call, and this is a deliberate
 * departure from the brief's own wording ("call `reportClientError` ... in
 * `runQuery`"), stated rather than quietly taken: `api/report.ts` imports
 * `sendErrorBeacon` from THIS module (it must — `tools/structural/
 * web-fetch-sites.test.ts` allows `fetch` nowhere else), so reporting from
 * here would add a SECOND import cycle to a file whose first one already
 * needs ten lines of justification above. `hooks.ts` imports both and is
 * imported by neither, so putting the two report calls side by side there
 * costs nothing structurally and makes the "fixed on one path, missed on
 * the other" failure this item exists to prevent visible at a glance.
 */
export async function runQuery<R>(name: string, params: unknown, result: z.ZodType<R>): Promise<R> {
  const search = new URLSearchParams({ params: canonicalize(params as JsonValue) })
  const response = await fetch(`/api/q/${encodeURIComponent(name)}?${search.toString()}`, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  })
  if (!response.ok) {
    const { message, reason } = await readError(response)
    throw new ApiError(message, response.status, reason)
  }
  const parsed = result.safeParse(await response.json())
  if (!parsed.success) throw new ResultMismatchError(name, describeSchemaIssues(parsed.error))
  return parsed.data
}

export async function runCommand(name: string, request: CommandRequest): Promise<CommandResponse> {
  const response = await fetch(`/api/cmd/${encodeURIComponent(name)}`, {
    method: 'POST',
    // Required: the worker's third CSRF layer refuses `/api/cmd/*` without it
    // (415), closing the cross-site `<form enctype="text/plain">` trick.
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  })
  // The client half of the skew handshake (design §5.3; `pwa/skew.ts`'s own
  // top comment). Checked BEFORE the generic `!response.ok` branch below,
  // not INSTEAD of it: this still falls through to the ordinary `ApiError`
  // throw immediately after, because the caller (`commandHook`'s mutation,
  // `api/hooks.ts`) still needs a rejected promise rather than a normal
  // reply. The flip itself is what makes a below-floor command converge on
  // the SAME `UpgradeRequired` screen the boot/focus poll would have shown
  // — `AppShell` re-renders the instant this store changes, independent of
  // whether the mutation this came from ever resolves or rejects.
  if (response.status === 426) markUpgradeRequired()
  if (!response.ok) {
    const { message, reason } = await readError(response)
    throw new ApiError(message, response.status, reason)
  }
  return (await response.json()) as CommandResponse
}

/**
 * Exactly the four keys `GET /api/me` returns (design §4.1). Never the
 * Principal.
 *
 * Declared HERE, alongside the only function that constructs one, and
 * re-exported by `auth/session.tsx` so its existing importers
 * (`shell/landing.ts`, `shell/nav.ts`) are untouched. The other direction —
 * leaving it in `session.tsx` and importing it here — would have made this
 * module depend on a React module for a type; a `import type` is erased at
 * runtime so it would not have been a real cycle, but it would have pointed
 * the dependency arrow the wrong way round for a shape that is defined by
 * the wire, not by the hook that happens to read it.
 */
export interface MeResponse {
  readonly kind: PrincipalKind
  readonly capabilities: readonly Capability[]
  readonly scope: { readonly sites: 'all' | readonly string[]; readonly clientPartyId?: string }
  readonly label: string | null
}

/**
 * `GET /api/me`, single-flight. Two callers want this at boot —
 * `auth/session.tsx`'s query and `outbox/clearance.ts`'s independent check —
 * and before this they made two separate requests.
 *
 * A module-scope promise, NOT the react-query cache: `beginClearanceCheck()`
 * is called from `main.tsx` at module scope, outside React entirely, with
 * no `QueryClientProvider` in scope. A reader who reaches for the cache here
 * will reintroduce the second fetch.
 *
 * Cleared on settle so a later sign-in/sign-out gets a fresh answer rather
 * than replaying a stale one for the page's whole life.
 */
let meInFlight: Promise<MeResponse | null> | null = null

/**
 * Drops the in-flight `/api/me` promise so the NEXT `fetchMe()` issues a
 * genuinely new request — final-review bucket 1, item E, REPRODUCED rather
 * than reasoned about (transcript in this fix wave's report).
 *
 * The single-flight above exists to collapse the two callers that want
 * `/api/me` AT BOOT (`auth/session.tsx`'s query and `outbox/clearance.ts`'s
 * detector) into one request. It clears itself on settle, so in the ordinary
 * case there is nothing to invalidate. The case it gets wrong is a request
 * that spans a CREDENTIAL CHANGE.
 *
 * The measured failure, end to end: `main.tsx`'s `beginClearanceCheck()`
 * starts a `/api/me` at boot. It has not settled by the time the user
 * submits the login form (a slow or stalled connection — this app's normal
 * condition on a solar site). `LoginRoute` gets its `{ ok: true }`, awaits
 * `invalidateQueries(['session','me'])` — which resolves immediately,
 * because nothing is OBSERVING that key while the login screen is up — and
 * navigates to `/`. `AppShell` mounts, `useSession()` fires, its `queryFn`
 * is `fetchMe`, and `fetchMe` hands back the promise from the request that
 * left BEFORE the user had a session. That resolves `null`, `staleTime:
 * Infinity` and `retry: false` pin it, and `AppShell` bounces the user
 * straight back to `/login` after a sign-in the server accepted. Measured:
 * exactly ONE `GET /api/me` for the whole flow, and "Sign in / Email /
 * Password" back on screen.
 *
 * Called on a SUCCESSFUL `signIn`/`setPassword` and on `signOut` — the three
 * points at which this tab's credential changes and anything already on the
 * wire is answering the previous one. Not called on a FAILED sign-in:
 * nothing changed, and the boot request is still the right answer.
 *
 * Does NOT abort the outstanding request, deliberately. `clearance.ts`'s
 * detector asked whether a session existed BEFORE this sign-in, and that is
 * still the question it wants answered — it holds its own reference to the
 * promise and is unaffected. Only the SHARING stops.
 */
function invalidateMeSingleFlight(): void {
  meInFlight = null
}

export function fetchMe(): Promise<MeResponse | null> {
  meInFlight ??= (async () => {
    const response = await fetch('/api/me', { headers: { accept: 'application/json' } })
    if (response.status === 401) return null
    if (!response.ok) {
      const { message, reason } = await readError(response)
      throw new ApiError(message, response.status, reason)
    }
    return (await response.json()) as MeResponse
  })().finally(() => {
    meInFlight = null
  })
  return meInFlight
}

/**
 * `GET /api/last-received` — `outbox/clearance.ts`'s boundary figure
 * (`packages/api/src/worker.ts` has the server-side rationale).
 *
 * THROWS on any failure rather than resolving a `null` that a genuine "no
 * events recorded" answer also produces — `outbox/clearance.ts`'s
 * `fetchLastReceivedAt` doc comment carries the full reasoning for why that
 * distinction is load-bearing, and keeping the throw here is what preserves
 * it now the transport has moved.
 */
export async function fetchLastReceived(): Promise<{ lastReceivedAt: string | null }> {
  const response = await fetch('/api/last-received', { headers: { accept: 'application/json' } })
  if (!response.ok) {
    const { message, reason } = await readError(response)
    throw new ApiError(message, response.status, reason)
  }
  const body = (await response.json()) as { lastReceivedAt?: string | null }
  return { lastReceivedAt: body.lastReceivedAt ?? null }
}

/**
 * `GET /api/version` — the poll half of the skew handshake. Throws like every
 * other read here; `pwa/skew.ts`'s `checkVersion` swallows that throw on
 * purpose ("offline is not evidence of skew"), which is the same observable
 * outcome its previous `if (!response.ok) return` produced.
 *
 * Both fields are normalised rather than cast straight through: the route
 * (`worker.ts`) always answers both, but a client that trusts a response
 * shape it never validated is exactly the class of bug
 * `test/error-reporting.test.tsx` engineers its crash from, so `??` gives
 * each one a defined value instead of letting `undefined` through a cast.
 */
export async function fetchVersion(): Promise<{ coreVersion: string; minClientBuild: string | null }> {
  const response = await fetch('/api/version', { headers: { accept: 'application/json' } })
  if (!response.ok) {
    const { message, reason } = await readError(response)
    throw new ApiError(message, response.status, reason)
  }
  const body = (await response.json()) as { coreVersion?: string; minClientBuild?: string | null }
  return { coreVersion: body.coreVersion ?? '', minClientBuild: body.minClientBuild ?? null }
}

/**
 * Sentence for a transport-level `fetch` rejection on the pre-session auth
 * screens — web-pre-deploy item 3. No `Response` object exists at all in
 * this case (there is nothing to read a status or body from), and the
 * realistic real cause is an installed PWA attempting sign-in with no
 * signal at a site — or, since `REQUEST_TIMEOUT_MS` reached these two, a
 * request accepted and never answered, which used to leave the button
 * spinning forever with nothing to read. Never the raw `TypeError: Failed to
 * fetch` `fetch`
 * itself throws — that string names an implementation detail, not a
 * situation, and CLAUDE.md forbids a developer identifier on a user-facing
 * screen. Worded so a site manager knows to retry once back in signal,
 * rather than assume their credentials or setup code were wrong.
 */
const CANNOT_REACH_SERVER = "Can't reach the server. Check your connection and try again."

/**
 * Sentence for an error `Response` that exists but whose body could not be
 * turned into the server's own wording — either `.json()` itself threw (the
 * body was not JSON at all, e.g. a proxy's HTML error page in front of the
 * worker) or the parsed body had no usable `error` string. Derived from the
 * STATUS, never printing it: a 5xx is the server's own fault, worth a short
 * wait rather than a retry with different input, so it gets a fixed sentence
 * distinct from the caller-supplied `fallback` — the words a signal-typed 4xx
 * uses to tell the site manager to check what they entered instead.
 */
function describeAuthFailure(status: number, fallback: string): string {
  return status >= 500 ? 'The server is having a problem right now. Try again in a few minutes.' : fallback
}

/**
 * `POST /api/auth/sign-in`.
 *
 * web-pre-deploy item 3: the `fetch` itself is wrapped so a transport
 * rejection (no `Response` at all) resolves `{ ok: false, message:
 * CANNOT_REACH_SERVER }` instead of propagating — previously that rejection
 * reached `LoginRoute`'s own `catch` and rendered the raw exception string.
 * Once a `Response` exists, `ok` is checked BEFORE `.json()` — the opposite
 * of the ordering this function shipped with (`auth/LoginRoute.tsx`'s
 * original inline `fetch`, preserved as-is by the task that moved it here) —
 * and the parse itself is guarded too: a body that is not JSON (a 502 HTML
 * error page from an edge in front of the worker was the concrete case that
 * named this bug) or JSON without a string `error` field falls back to
 * `describeAuthFailure` rather than the parse exception. Only when the
 * server's OWN `error` string is genuinely present is it returned verbatim —
 * `LoginRoute`'s doc comment carries why that verbatim rendering matters.
 */
export async function signIn(body: {
  email: string
  password: string
}): Promise<{ ok: true } | { ok: false; message: string }> {
  const fallback = 'Sign in failed. Check your details and try again.'
  let response: Response
  try {
    response = await fetch('/api/auth/sign-in', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })
  } catch {
    return { ok: false, message: CANNOT_REACH_SERVER }
  }
  if (!response.ok) {
    try {
      const parsed = (await response.json()) as { error?: string }
      return {
        ok: false,
        message: typeof parsed.error === 'string' ? parsed.error : describeAuthFailure(response.status, fallback),
      }
    } catch {
      return { ok: false, message: describeAuthFailure(response.status, fallback) }
    }
  }
  // This tab's credential just changed — see `invalidateMeSingleFlight`.
  invalidateMeSingleFlight()
  return { ok: true }
}

/**
 * `POST /api/auth/set-password`. Same transport-catch, same `ok`-before-
 * `.json()` ordering, same guarded parse, and the same reason, as `signIn`
 * above — kept as its own independent implementation rather than a shared
 * helper so each function's fix stands on its own; see this task's report
 * for the mutation check that relies on that independence.
 */
export async function setPassword(body: {
  token: string
  password: string
}): Promise<{ ok: true } | { ok: false; message: string }> {
  const fallback = 'Could not set your password. Check the code and try again.'
  let response: Response
  try {
    response = await fetch('/api/auth/set-password', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })
  } catch {
    return { ok: false, message: CANNOT_REACH_SERVER }
  }
  if (!response.ok) {
    try {
      const parsed = (await response.json()) as { error?: string }
      return {
        ok: false,
        message: typeof parsed.error === 'string' ? parsed.error : describeAuthFailure(response.status, fallback),
      }
    } catch {
      return { ok: false, message: describeAuthFailure(response.status, fallback) }
    }
  }
  // This tab's credential just changed — see `invalidateMeSingleFlight`.
  // `POST /api/auth/set-password` signs the user in on success (the token
  // just redeemed is stronger evidence of identity than the password they
  // chose), so it is the same credential change `signIn` above is.
  invalidateMeSingleFlight()
  return { ok: true }
}

/**
 * `POST /api/auth/sign-out`, best-effort — NEVER throws, and never reports
 * whether anything answered.
 *
 * There is no server route behind this today; `shell/AppShell.tsx`'s own doc
 * comment on its `signOut` handler carries that finding in full, including
 * why the two lines it runs AFTER this call are what actually change what
 * the tab believes. That reasoning stays there, at the decision it explains
 * — only the request moved here.
 */
export async function signOut(): Promise<void> {
  // Before the request, not after: this tab has decided it is signed out
  // whether or not a server was reachable to be told, so an `/api/me` still
  // on the wire from before that decision must stop being shared either
  // way. See `invalidateMeSingleFlight`.
  invalidateMeSingleFlight()
  try {
    await fetch('/api/auth/sign-out', { method: 'POST' })
  } catch {
    // Swallowed here rather than at the call site so `signOut()` has one
    // meaning everywhere: the tab signs itself out whether or not a server
    // was reachable to be told.
  }
}

/**
 * `POST /api/client-error`, fire-and-forget — the deliberate NON-THROWING
 * exception in this file.
 *
 * Every other function here rejects on a fault, because a caller that asked
 * for something has somewhere to put the failure. This one is what the error
 * paths themselves call when something has ALREADY gone wrong, so routing it
 * through a wrapper that throws risks a fault in reporting raising a fault
 * that is then reported — a loop, or at best a second error with nowhere
 * better to go than back into this same function. `api/report.ts`'s own doc
 * comment states the same rule from the caller's side.
 *
 * `keepalive: true` so a report has a chance to survive the navigation a
 * crash often triggers (a reload, a redirect) — without it, the browser can
 * abort an in-flight fetch the instant the document starts navigating away,
 * which is exactly the moment this call is most likely to fire.
 */
export function sendErrorBeacon(body: unknown): void {
  void fetch('/api/client-error', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    keepalive: true,
    body: JSON.stringify(body),
  }).catch(() => {})
}

/**
 * Test-only: clears the `/api/me` single-flight promise. Plain, pure,
 * one-way — the same `resetRegistryForTests` reset contract `core`/
 * `contracts` established and this one genuinely keeps, which is why it
 * keeps the plain name where `pwa/skew.ts`'s and `outbox/clearance.ts`'s
 * subscriber-dropping resets no longer do (web-pre-deploy item 10). Called
 * from `test/setup.ts`'s `beforeEach` for the same reason those are: a
 * request still in flight when a test ends would
 * otherwise be handed, unsettled, to whichever test runs next, answering it
 * from the previous test's now-closed MSW server.
 */
export function resetClientForTests(): void {
  invalidateMeSingleFlight()
}
